import { z } from "zod";
import { localizedTextSchema, requiredLocalizedTextSchema, timestampsSchema } from "./common";
import { listQueryBaseSchema, queryBoolean } from "./list-query";
import { COMBO_TYPE } from "../constants/status";

const typeValues = Object.values(COMBO_TYPE) as [number, ...number[]];
const comboTypeSchema = z.union([
  z.literal(typeValues[0]),
  ...typeValues.slice(1).map((v) => z.literal(v)),
]);

export const comboPackageSchema = z.object({
  name: z.string().min(1),
  price: z.string().regex(/^\d+(\.\d+)?$/),
  includes: z.array(z.string()).default([]),
});
export type ComboPackage = z.infer<typeof comboPackageSchema>;

export const comboDtoSchema = z
  .object({
    id: z.string().uuid(),
    name: localizedTextSchema,
    description: localizedTextSchema,
    slug: z.string(),
    type: comboTypeSchema,
    cityId: z.string().uuid().nullable(),
    city: z.object({ id: z.string().uuid(), name: localizedTextSchema }).nullable(),
    price: z.string(),
    comparePrice: z.string(),
    discount: z.number().int(),
    isPromoted: z.boolean(),
    featured: z.boolean(),
    packages: z.array(comboPackageSchema),
    rating: z.string(),
    children: z.number().int(),
    infant: z.number().int(),
    activityIds: z.array(z.string().uuid()),
  })
  .extend(timestampsSchema.shape);
export type ComboDTO = z.infer<typeof comboDtoSchema>;

export const comboUpsertSchema = z.object({
  name: requiredLocalizedTextSchema,
  description: localizedTextSchema,
  slug: z.string().optional(),
  type: comboTypeSchema,
  cityId: z.string().uuid(),
  price: z.string().regex(/^\d+(\.\d+)?$/),
  comparePrice: z.string().regex(/^\d+(\.\d+)?$/),
  discount: z.number().int().min(0).max(100).default(0),
  isPromoted: z.boolean().default(false),
  featured: z.boolean().default(false),
  packages: z.array(comboPackageSchema).default([]),
  children: z.number().int().nonnegative().default(0),
  infant: z.number().int().nonnegative().default(0),
  activityIds: z.array(z.string().uuid()).default([]),
});
export type ComboUpsertInput = z.infer<typeof comboUpsertSchema>;

export const combosListQuerySchema = listQueryBaseSchema.extend({
  cityId: z.string().uuid().optional(),
  type: z.coerce.number().optional(),
  featured: queryBoolean(),
  isPromoted: queryBoolean(),
});
export type CombosListQuery = z.infer<typeof combosListQuerySchema>;
